import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ExclamationTriangleIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
const FRAME_RATE_PRESETS = [1, 2, 5, 10];

interface ModuleInfo {
  type: string | null;
  is_default: boolean;
  module_type: string;
  status: string;
  model_paths: string[];
  error: string | null;
}

interface DetectionResult {
  class: string;
  confidence: number;
  bbox: { x: number; y: number; width: number; height: number };
  keypoints: { x: number; y: number; visibility?: number; confidence?: number }[];
}

interface FgsResult {
  total: number;
  maxTotal: number;
  analgesiaRecommended: boolean;
  summary: string;
  actionUnits: Record<string, { score: number; description: string }>;
}

interface FrameResult {
  timestamp: number;
  dataUrl: string;
  width: number;
  height: number;
  detections: DetectionResult[];
  count: number;
  fgs: FgsResult | null;
  latencyMs: number;
  error?: string;
}

const ANALYSIS_LABELS: Record<string, string> = {
  'cat-pose': 'Cat Pain Assessment (FGS)',
  'dog-pose': 'Dog Pose & Behavior',
  'yolo-generic': 'Generic YOLO Detection',
  'vomit-detect': 'Vomit Analysis',
  'poop-detect': 'Poop Analysis',
};

const detectFrame = async (dataUrl: string, module: string, token: string | null): Promise<{ detections: DetectionResult[]; count: number; fgs: FgsResult | null }> => {
  const blob = await (await fetch(dataUrl)).blob();
  const formData = new FormData();
  formData.append('file', blob, 'frame.jpg');
  if (module) formData.append('module', module);

  const res = await fetch(`${API_URL}/api/dashboard/detect`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `HTTP ${res.status}`);
  }

  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Detection failed');

  return {
    detections: data.detections || [],
    count: data.count || 0,
    fgs: data.fgs,
  };
};

const DetectView: React.FC<{ onNavigateHome?: () => void }> = ({ onNavigateHome }) => {
  const [modules, setModules] = useState<Record<string, ModuleInfo>>({});
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageDims, setImageDims] = useState<{ w: number; h: number } | null>(null);
  const [result, setResult] = useState<{ detections: DetectionResult[]; count: number; fgs: FgsResult | null } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [isVideo, setIsVideo] = useState(false);
  const [frameResults, setFrameResults] = useState<FrameResult[]>([]);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [frameRate, setFrameRate] = useState(1);
  const [frameRateInput, setFrameRateInput] = useState('1');
  const [videoDuration, setVideoDuration] = useState(0);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingVideoUrl, setPendingVideoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await fetch(`${API_URL}/api/dashboard/model-health`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          if (data.modules) {
            const available = Object.entries(data.modules as Record<string, ModuleInfo>)
              .filter(([, info]) => info.status !== 'error' && info.status !== 'skipped');
            const moduleMap = Object.fromEntries(available);
            setModules(moduleMap);
            const defaultMod = available.find(([, info]) => info.is_default);
            if (defaultMod) {
              setSelectedModule(defaultMod[0]);
            } else if (available.length > 0) {
              setSelectedModule(available[0][0]);
            }
          }
        }
      } catch {
        setSelectedModule('cat-pose');
      } finally {
        setModulesLoading(false);
      }
    };
    fetchModules();
  }, [token]);

  const resetState = () => {
    setImageUrl(null);
    setResult(null);
    setError(null);
    setImageDims(null);
    setIsVideo(false);
    setFrameResults([]);
    setCurrentFrame(0);
    setProgress(0);
    setProgressText('');
    setVideoDuration(0);
    setPendingFile(null);
    if (pendingVideoUrl) URL.revokeObjectURL(pendingVideoUrl);
    setPendingVideoUrl(null);
  };

  const handleFileSelected = async (file: File) => {
    // Clear previous results but keep frame rate setting
    if (pendingVideoUrl) URL.revokeObjectURL(pendingVideoUrl);
    setImageUrl(null);
    setResult(null);
    setError(null);
    setImageDims(null);
    setIsVideo(false);
    setFrameResults([]);
    setCurrentFrame(0);
    setProgress(0);
    setProgressText('');
    setVideoDuration(0);
    setPendingFile(file);

    if (file.type.startsWith('video/')) {
      setIsVideo(true);
      const url = URL.createObjectURL(file);
      setPendingVideoUrl(url);

      const video = document.createElement('video');
      video.src = url;
      video.muted = true;
      video.playsInline = true;

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Failed to load video'));
      }).catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to load video');
      });

      if (video.duration && !Number.isNaN(video.duration)) {
        setVideoDuration(video.duration);
        video.currentTime = 0.1;
        await new Promise<void>((resolve) => {
          video.onseeked = () => resolve();
        });
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')?.drawImage(video, 0, 0);
        setImageUrl(canvas.toDataURL('image/jpeg', 0.7));
        setImageDims({ w: video.videoWidth, h: video.videoHeight });
      }
    } else if (file.type.startsWith('image/')) {
      setImageUrl(URL.createObjectURL(file));
    } else {
      setError('Unsupported file type. Upload an image or video.');
      setPendingFile(null);
    }
  };

  const handleAnalyze = useCallback(async () => {
    if (!pendingFile) return;
    setIsAnalyzing(true);
    setError(null);
    setResult(null);
    setFrameResults([]);

    if (isVideo) {
      await runVideoAnalysis(pendingFile);
    } else {
      await runImageAnalysis(pendingFile);
    }
  }, [pendingFile, isVideo, selectedModule, token, frameRate]);

  const runImageAnalysis = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedModule) formData.append('module', selectedModule);

      const res = await fetch(`${API_URL}/api/dashboard/detect`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Detection failed');

      setResult({
        detections: data.detections || [],
        count: data.count || 0,
        fgs: data.fgs,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Detection failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const runVideoAnalysis = async (file: File) => {
    const videoUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = videoUrl;
    video.muted = true;
    video.playsInline = true;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Failed to load video'));
    });

    const duration = video.duration;
    setVideoDuration(duration);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Canvas not supported');
      setIsAnalyzing(false);
      return;
    }

    const frameInterval = 1 / frameRate;
    const totalFrames = Math.floor(duration / frameInterval);
    const results: FrameResult[] = [];

    for (let i = 0; i < totalFrames; i++) {
      const t = i * frameInterval;
      setProgressText(`Extracting frame ${i + 1}/${totalFrames} (${t.toFixed(1)}s)`);
      setProgress(((i + 1) / totalFrames) * 30);

      video.currentTime = t;
      await new Promise<void>((resolve) => {
        video.onseeked = () => resolve();
      });

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

      setProgressText(`Detecting frame ${i + 1}/${totalFrames} (${t.toFixed(1)}s)`);
      setProgress(30 + ((i + 1) / totalFrames) * 70);

      const t0 = performance.now();
      try {
        const det = await detectFrame(dataUrl, selectedModule, token);
        const latency = performance.now() - t0;
        results.push({
          timestamp: t,
          dataUrl,
          width: video.videoWidth,
          height: video.videoHeight,
          detections: det.detections,
          count: det.count,
          fgs: det.fgs,
          latencyMs: latency,
        });
      } catch (err) {
        const latency = performance.now() - t0;
        results.push({
          timestamp: t,
          dataUrl,
          width: video.videoWidth,
          height: video.videoHeight,
          detections: [],
          count: 0,
          fgs: null,
          latencyMs: latency,
          error: err instanceof Error ? err.message : 'Detection failed',
        });
      }

      setFrameResults([...results]);
    }

    if (results.length > 0) {
      setCurrentFrame(0);
      setImageUrl(results[0].dataUrl);
      setImageDims({ w: results[0].width, h: results[0].height });
      setResult({
        detections: results[0].detections,
        count: results[0].count,
        fgs: results[0].fgs,
      });
    }

    setProgress(100);
    setProgressText(`Done — ${results.length} frames analyzed`);
    setIsAnalyzing(false);
    URL.revokeObjectURL(videoUrl);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) handleFileSelected(file);
  };

  const handleImgLoad = () => {
    if (imgRef.current) {
      setImageDims({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    }
  };

  const selectFrame = (idx: number) => {
    if (idx < 0 || idx >= frameResults.length) return;
    setCurrentFrame(idx);
    const fr = frameResults[idx];
    setImageUrl(fr.dataUrl);
    setImageDims({ w: fr.width, h: fr.height });
    setResult({ detections: fr.detections, count: fr.count, fgs: fr.fgs });
    setError(fr.error || null);
  };

  const detColor = (cls: string) => {
    if (cls === 'cat-face') return '#fbbf24';
    if (cls === 'cat-eye') return '#34d399';
    if (cls === 'food') return '#f59e0b';
    if (cls === 'hairball') return '#a78bfa';
    if (cls === 'poop') return '#84cc16';
    if (cls === 'others') return '#9ca3af';
    return '#60a5fa';
  };

  const renderOverlay = () => {
    if (!result || !result.detections.length || !imageDims) return null;
    const { w: imgW, h: imgH } = imageDims;

    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${imgW} ${imgH}`} preserveAspectRatio="xMidYMid meet">
        {result.detections.map((det, di) => {
          const color = detColor(det.class);
          const b = det.bbox;
          if (b) {
            const label = `${det.class} ${(det.confidence * 100).toFixed(0)}%`;
            const labelWidth = label.length * (Math.max(imgW, imgH) / 55);
            return (
              <g key={`bbox-${di}`}>
                <rect
                  x={b.x - b.width / 2}
                  y={b.y - b.height / 2}
                  width={b.width}
                  height={b.height}
                  fill="none"
                  stroke={color}
                  strokeWidth={Math.max(imgW, imgH) / 300}
                  opacity={0.7}
                  rx={4}
                />
                <rect
                  x={b.x - b.width / 2}
                  y={Math.max(0, b.y - b.height / 2 - Math.max(imgW, imgH) / 28)}
                  width={labelWidth}
                  height={Math.max(imgW, imgH) / 28}
                  fill={color}
                  opacity={0.85}
                  rx={3}
                />
                <text
                  x={b.x - b.width / 2 + 4}
                  y={Math.max(0, b.y - b.height / 2 - Math.max(imgW, imgH) / 28) + Math.max(imgW, imgH) / 40}
                  fill="#fff"
                  fontSize={Math.max(imgW, imgH) / 55}
                  fontWeight="600"
                >
                  {label}
                </text>
              </g>
            );
          }
          return null;
        })}
        {result.detections.map((det, di) => {
          const color = detColor(det.class);
          return (det.keypoints || []).map((kp, ki) => {
            if (kp.x < 0 || kp.y < 0 || kp.x > imgW || kp.y > imgH) return null;
            return (
              <circle
                key={`kp-${di}-${ki}`}
                cx={kp.x}
                cy={kp.y}
                r={Math.max(imgW, imgH) / 400}
                fill={color}
                opacity={kp.visibility ?? kp.confidence ?? 1}
              />
            );
          });
        })}
      </svg>
    );
  };

  const fgsScores = frameResults.map(f => ({ t: f.timestamp, total: f.fgs?.total ?? null }));

  const moduleNames = Object.keys(modules);

  return (
    <div className="flex-1 flex flex-col dark:bg-claude-darkBg bg-claude-bg h-full">
      <div className="draggable flex h-12 items-center justify-between px-4 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
        <div className="non-draggable flex items-center gap-3">
          <button
            onClick={onNavigateHome}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">Upload & Analyze</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-white dark:bg-claude-darkSurface rounded-xl shadow-sm border border-gray-200 dark:border-claude-darkBorder p-4">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 dark:text-claude-darkText">AI Analysis</h2>
                  <p className="text-sm text-gray-500 dark:text-claude-darkTextSecondary">
                    {isAnalyzing ? progressText : result ? `${result.count} detection${result.count !== 1 ? 's' : ''}` : 'Upload an image or video to analyze'}
                    {isVideo && !isAnalyzing && frameResults.length > 0 && ` • ${frameResults.length} frames`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">Model:</label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  disabled={isAnalyzing || modulesLoading}
                  className="px-3 py-1.5 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {modulesLoading && <option>Loading...</option>}
                  {!modulesLoading && moduleNames.length === 0 && <option value="cat-pose">Cat Pain Assessment</option>}
                  {moduleNames.map((name) => (
                    <option key={name} value={name}>
                      {ANALYSIS_LABELS[name] || name}
                      {modules[name]?.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Frame rate selector — always visible so user can set before uploading video */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">Frame rate (for video):</label>
                <div className="flex items-center gap-1">
                  {FRAME_RATE_PRESETS.map((fps) => (
                    <button
                      key={fps}
                      onClick={() => { setFrameRate(fps); setFrameRateInput(String(fps)); }}
                      disabled={isAnalyzing}
                      className={`px-2 py-1 rounded-md text-xs font-medium transition-colors border ${
                        frameRate === fps
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400'
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {fps} fps
                    </button>
                  ))}
                  <input
                    type="number"
                    min={0.5}
                    max={120}
                    step={0.5}
                    value={frameRateInput}
                    onChange={(e) => {
                      setFrameRateInput(e.target.value);
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v > 0 && v <= 120) setFrameRate(v);
                    }}
                    onBlur={() => {
                      const v = parseFloat(frameRateInput);
                      if (isNaN(v) || v <= 0 || v > 120) {
                        setFrameRateInput(String(frameRate));
                      } else {
                        setFrameRateInput(String(v));
                      }
                    }}
                    disabled={isAnalyzing}
                    className="w-16 px-2 py-1 border border-gray-300 dark:border-claude-darkBorder rounded-md text-xs text-center dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-400">fps</span>
                </div>
              </div>
              {videoDuration > 0 && !isAnalyzing && frameResults.length === 0 && (
                <span className="text-xs text-gray-400">
                  ~{Math.floor(videoDuration * frameRate)} frames will be analyzed
                </span>
              )}
            </div>

            {/* Performance stats after video analysis */}
            {isVideo && !isAnalyzing && frameResults.length > 0 && (
              <div className="flex items-center gap-4 mb-3 text-xs text-gray-500 dark:text-gray-400">
                <span>{frameResults.length} frames</span>
                <span>·</span>
                <span>avg {Math.round(frameResults.reduce((a, f) => a + f.latencyMs, 0) / frameResults.length)}ms/frame</span>
                <span>·</span>
                <span>total {Math.round(frameResults.reduce((a, f) => a + f.latencyMs, 0) / 1000)}s</span>
                <span>·</span>
                <span>{frameRate} fps</span>
              </div>
            )}

            {(isAnalyzing && progress > 0) && (
              <div className="mb-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <div
              className="relative bg-gray-900 dark:bg-black rounded-lg overflow-hidden"
              style={{ minHeight: '20rem' }}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {imageUrl ? (
                <div className="relative w-full h-full">
                  <img
                    ref={imgRef}
                    src={imageUrl}
                    alt="Analyzed"
                    className="w-full h-full object-contain"
                    onLoad={handleImgLoad}
                  />
                  {renderOverlay()}
                  {isAnalyzing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <div className="text-center">
                        <div className="animate-spin w-10 h-10 border-2 border-white border-t-transparent rounded-full mx-auto mb-2"></div>
                        <p className="text-white text-sm">{progressText || 'Running detection...'}</p>
                      </div>
                    </div>
                  )}
                  {error && !isAnalyzing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-red-900/60">
                      <p className="text-white text-sm px-4 text-center">{error}</p>
                    </div>
                  )}
                  <button
                    onClick={resetState}
                    className="absolute top-2 right-2 px-2 py-1 bg-black/60 text-white rounded text-xs hover:bg-black/80 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="mx-auto mb-4 w-20 h-20 rounded-full bg-blue-500/20 border-2 border-blue-500/40 flex items-center justify-center hover:bg-blue-500/30 transition-colors"
                    >
                      <ArrowUpTrayIcon className="w-8 h-8 text-blue-400" />
                    </button>
                    <p className="text-gray-300 text-sm font-medium">Drop an image or video here or click to upload</p>
                    <p className="text-gray-500 text-xs mt-1">JPG, PNG, WEBP, MP4, MOV, WEBM</p>
                  </div>
                </div>
              )}
            </div>

            {pendingFile && !isAnalyzing && (
              <div className="mt-3 flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {isVideo
                    ? `${pendingFile.name} — ${videoDuration.toFixed(1)}s • ~${Math.floor(videoDuration * frameRate)} frames at ${frameRate} fps`
                    : pendingFile.name}
                </div>
                <button
                  onClick={handleAnalyze}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  {result ? 'Re-analyze' : 'Analyze'}
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleInputChange}
              className="hidden"
            />
          </div>

          {isVideo && frameResults.length > 1 && !isAnalyzing && (
            <div className="bg-white dark:bg-claude-darkSurface rounded-xl shadow-sm border border-gray-200 dark:border-claude-darkBorder p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-claude-darkText">Frame Scrubber</h3>
                <span className="text-sm text-gray-500">
                  Frame {currentFrame + 1}/{frameResults.length} ({frameResults[currentFrame].timestamp.toFixed(1)}s)
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={frameResults.length - 1}
                value={currentFrame}
                onChange={(e) => selectFrame(parseInt(e.target.value))}
                className="w-full"
              />
              {fgsScores.some(s => s.total !== null) && (
                <div className="mt-4">
                  <div className="text-sm text-gray-500 mb-2">FGS Score Timeline</div>
                  <div className="flex items-end gap-1 h-24">
                    {fgsScores.map((s, i) => (
                      <div
                        key={i}
                        className={`flex-1 rounded-t cursor-pointer transition-all ${
                          i === currentFrame ? 'ring-2 ring-blue-500' : 'opacity-60 hover:opacity-100'
                        }`}
                        style={{
                          height: s.total !== null ? `${(s.total / 10) * 100}%` : '4%',
                          backgroundColor: s.total === null ? '#9ca3af' : s.total >= 4 ? '#ef4444' : s.total >= 2 ? '#f59e0b' : '#22c55e',
                        }}
                        onClick={() => selectFrame(i)}
                        title={`${s.t.toFixed(1)}s: ${s.total ?? 'no detection'}`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0s</span>
                    <span>{frameResults[frameResults.length - 1].timestamp.toFixed(1)}s</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="grid grid-cols-1 gap-4">
              {result.fgs && (
                <div className="bg-white dark:bg-claude-darkSurface rounded-xl shadow-sm border border-gray-200 dark:border-claude-darkBorder p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-claude-darkText mb-3">Feline Grimace Scale</h3>
                  <div className="mb-4">
                    <div className={`text-4xl font-bold ${result.fgs.total >= 4 ? 'text-red-500' : 'text-green-500'}`}>
                      {result.fgs.total}
                      <span className="text-lg text-gray-400">/{result.fgs.maxTotal}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{result.fgs.summary}</p>
                    {result.fgs.analgesiaRecommended && (
                      <p className="text-sm text-red-500 font-medium mt-1">⚠ Analgesia recommended</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    {Object.entries(result.fgs.actionUnits).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">{val.description}</span>
                          <span className={`text-sm font-semibold w-6 text-center rounded px-1 ${
                            val.score === 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            val.score === 1 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {val.score}
                          </span>
            </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white dark:bg-claude-darkSurface rounded-xl shadow-sm border border-gray-200 dark:border-claude-darkBorder p-4">
                <h3 className="font-semibold text-gray-900 dark:text-claude-darkText mb-3">Detections</h3>
                <div className="space-y-2">
                  {result.detections.map((det, i) => {
                    const isBboxOnly = ['food', 'hairball', 'others', 'poop'].includes(det.class);
                    return (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: detColor(det.class) }} />
                          {det.class.replace(/-/g, ' ')}
                        </span>
                        <span className="text-sm text-gray-500">
                          {(det.confidence * 100).toFixed(1)}% confidence
                          {isBboxOnly && det.bbox
                            ? ` · region ${Math.round(det.bbox.width)}×${Math.round(det.bbox.height)}px`
                            : ` · ${det.keypoints?.length || 0} keypoints`}
                        </span>
                      </div>
                    );
                  })}
                  {result.detections.length === 0 && (
                    <p className="text-sm text-gray-400">No detections for this {isVideo ? 'frame' : 'image'}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetectView;